/**
 * SIL-516 — pure layout math for `FlowSankey`, split out from the React
 * component so the conservation/floor invariants below are unit-testable
 * without touching the DOM. Ported from the prototype's ~150-line
 * `render()` (design-eval artifact, see LifecycleSankey.tsx for the link):
 * generic columns/nodes/links, ONE linear scale per diagram, a small pixel
 * floor for near-zero flows (never applied to the figures themselves — see
 * `floored` below), and per-column label collision handling.
 *
 * Honesty rules this module enforces:
 * - One `scale` for the whole diagram — every node/link height is
 *   `value * scale`, clamped up to `floorPx` only for *rendering* (a flow
 *   that is genuinely ~0 still needs a visible sliver). `floored` is exposed
 *   on both nodes and links precisely so a caller can annotate "≈0" instead
 *   of silently letting the floor read as a real magnitude.
 * - Conservation: a node's value is `max(sum(in), sum(out))`, so a node fed
 *   by one stage and feeding the next renders one consistent size on both
 *   sides — callers are responsible for the figures reconciling upstream
 *   (fleet total = sum of legs, each leg = sum of its outcomes); this module
 *   only lays out whatever values it is given.
 */

export interface FlowSankeyNodeSpec {
  id: string;
  /** Column index; columns are sorted ascending and spaced per `colPos` (or evenly). */
  col: number;
  /** Explicit value for root/leaf nodes with no links on the relevant side (e.g. the
   * left-most "Fleet" node). When omitted, derived from `max(sum(in), sum(out))`. */
  value?: number;
  /** Node display label. Omit for an unlabeled mid-column node (tooltip-only). */
  label?: string;
  cap?: string;
  subText?: string;
  fillClass: string;
  striped?: boolean;
  /** SIL-459 — renders inside the dashed advisory quarantine boundary. */
  quarantine?: boolean;
  tip?: string;
}

export interface FlowSankeyLinkSpec {
  source: string;
  target: string;
  value: number;
  fillClass: string;
  striped?: boolean;
  tip?: string;
}

export interface FlowSankeySpec {
  nodes: FlowSankeyNodeSpec[];
  links: FlowSankeyLinkSpec[];
  /** Total viewBox width/height in SVG user units. */
  width: number;
  height: number;
  /** Right margin reserved for direct labels. */
  rightMargin?: number;
  leftMargin?: number;
  nodeWidth?: number;
  /** Vertical gap between stacked nodes in the same column. */
  gapPx?: number;
  /** Rendering-only pixel floor for near-zero values (never applied to figures). */
  floorPx?: number;
  topPad?: number;
  bottomPad?: number;
  /** Column id -> 0..1 fraction across the plot width. Enables time-proportional axes. */
  colPos?: Record<number, number>;
}

export interface FlowSankeyLayoutNode extends FlowSankeyNodeSpec {
  value: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  /** Rendered height in px, floor-clamped. */
  heightPx: number;
  /** True if the true (unclamped) height fell below `floorPx` — the node's
   * on-screen size is larger than its true share; annotate "≈0" rather than
   * let it read as a real magnitude. */
  floored: boolean;
  /** True only when the value's share of its scale total is genuinely
   * negligible (< 0.1%) — the "annotate ≈0" signal. A flow can be `floored`
   * (too thin to draw at true scale) without being negligible. */
  negligible: boolean;
  /** Suggested y for the first label line (post collision pass). */
  labelY: number;
  /** SIL-633 — how many lines of the label block actually fit in the vertical
   * gap to the next label in this column. Lines beyond this are dropped (the
   * tooltip carries them), the same budget-then-tooltip rule the horizontal
   * `*Display` truncation applies. Always >= 1. */
  labelLines: number;
  /** SIL-632 — `label` truncated to the horizontal budget between this column
   * and the next (labels are user-authored free text and used to run over the
   * next column). `undefined` when the budget is too small to be worth an
   * ellipsis: the caller drops that line and shifts the rest up. The
   * untruncated originals stay available for the hover tooltip. */
  labelDisplay?: string;
  /** `subText` (or the formatted value) truncated to the same budget. Every
   * line of the label block is user text in some caller — the value line is
   * "month-0 FMV · acts month 9" as often as it is a bare number. */
  subTextDisplay?: string;
  /** `cap` truncated to the same budget. */
  capDisplay?: string;
}

export interface FlowSankeyLayoutLink extends FlowSankeyLinkSpec {
  path: string;
  heightPx: number;
  floored: boolean;
  negligible: boolean;
}

export interface FlowSankeyLayout {
  nodes: FlowSankeyLayoutNode[];
  links: FlowSankeyLayoutLink[];
  width: number;
  height: number;
  cols: number[];
  colX: Map<number, number>;
  /** One linear scale (px per unit value) shared by every node/link. */
  scale: number;
}

const DEFAULT_NODE_WIDTH = 10;
const DEFAULT_GAP = 14;
const DEFAULT_FLOOR = 3;
/** Share of the scale total below which a flow is annotated "\u22480". */
const NEGLIGIBLE_SHARE = 0.001;
const DEFAULT_TOP_PAD = 26;
const DEFAULT_BOTTOM_PAD = 16;
const DEFAULT_LEFT_MARGIN = 4;
const DEFAULT_RIGHT_MARGIN = 190;

/** SIL-632 — node label geometry. Labels are drawn at `n.x1 + LABEL_OFFSET_PX`. */
const LABEL_OFFSET_PX = 8;
/** `text-xs` — the class the node label and column header are rendered with. */
const LABEL_FONT_PX = 12;
/** The value line renders a touch larger, and often in the mono `font-num`. */
const SUBTEXT_FONT_PX = 13;
const CAP_FONT_PX = 12;
/** Breathing room between a truncated label and the next column's nodes. */
const LABEL_GUTTER_PX = 6;
/** Below this many characters an ellipsised label is a smudge, not a word —
 * drop the label line and let the value sub-line carry the node. */
const LABEL_MIN_CHARS = 6;
/** Column headers closer than this to the previously-drawn one are dropped.
 * They are axis ticks; a missing tick between two close action months costs
 * less than two overlapping ones. */
const COL_LABEL_MIN_GAP_PX = 56;
/** Vertical advance between the lines of a node's label block — must match the
 * `y + i * 13` step `FlowSankey` draws them at. */
const LABEL_LINE_H = 13;
/** A squeezed label block still keeps its first line. */
const LABEL_MIN_BLOCK_PX = LABEL_LINE_H + 2;

/** Lines a node's label block wants: label, value, and the optional cap. */
function desiredLabelLines(n: { cap?: string }): number {
  return n.cap ? 3 : 2;
}

/**
 * Approximate rendered width of `text`. SVG has no layout pass before render
 * and `getComputedTextLength` needs a second pass with a ref per label, so
 * this stays a heuristic: ~0.55em average advance for the mixed-case UI text
 * these charts carry. Approximate by a character either way — the fallback
 * for anything cut is the hover tooltip, which carries the full string.
 */
export function approxTextWidthPx(text: string, fontSizePx: number): number {
  return text.length * fontSizePx * 0.55;
}

/**
 * Truncate `text` with an ellipsis to fit `budgetPx`. Returns `undefined` when
 * fewer than `LABEL_MIN_CHARS` characters survive.
 */
export function truncateToWidth(text: string, budgetPx: number, fontSizePx = LABEL_FONT_PX): string | undefined {
  if (approxTextWidthPx(text, fontSizePx) <= budgetPx) return text;
  const maxChars = Math.floor(budgetPx / (fontSizePx * 0.55)) - 1; // -1 for the ellipsis
  if (maxChars < LABEL_MIN_CHARS) return undefined;
  return `${text.slice(0, maxChars).trimEnd()}…`;
}

/**
 * SIL-632 — which column headers to draw. Time-proportional columns can land
 * a few px apart (two cohorts acting at M6 and M9 on a 36-month axis), and
 * every header was previously drawn unconditionally on top of its neighbour.
 * Keeps the first column always, then drops any header whose drawn extent
 * would come within `COL_LABEL_MIN_GAP_PX` of the last one kept.
 */
export function visibleColLabelCols(
  cols: number[],
  colX: Map<number, number>,
  colLabels: Record<number, string>,
  fontSizePx = LABEL_FONT_PX,
): number[] {
  const kept: number[] = [];
  let lastRight = -Infinity;
  cols.forEach((c) => {
    const label = colLabels[c];
    if (!label) return;
    const x = colX.get(c) ?? 0;
    if (kept.length > 0 && x - lastRight < COL_LABEL_MIN_GAP_PX) return;
    kept.push(c);
    lastRight = x + approxTextWidthPx(label, fontSizePx) / 2;
  });
  return kept;
}

export function computeFlowSankeyLayout(spec: FlowSankeySpec): FlowSankeyLayout {
  const {
    nodes: nodeSpecs,
    links: linkSpecs,
    width,
    height,
    rightMargin = DEFAULT_RIGHT_MARGIN,
    leftMargin = DEFAULT_LEFT_MARGIN,
    nodeWidth = DEFAULT_NODE_WIDTH,
    gapPx = DEFAULT_GAP,
    floorPx = DEFAULT_FLOOR,
    topPad = DEFAULT_TOP_PAD,
    bottomPad = DEFAULT_BOTTOM_PAD,
    colPos,
  } = spec;

  const cols = [...new Set(nodeSpecs.map((n) => n.col))].sort((a, b) => a - b);
  const colX = new Map<number, number>();
  cols.forEach((c, i) => {
    const frac = colPos ? colPos[c] ?? i / Math.max(1, cols.length - 1) : i / Math.max(1, cols.length - 1);
    colX.set(c, leftMargin + (width - leftMargin - rightMargin - nodeWidth) * frac);
  });

  const inLinks = new Map<string, FlowSankeyLinkSpec[]>();
  const outLinks = new Map<string, FlowSankeyLinkSpec[]>();
  nodeSpecs.forEach((n) => {
    inLinks.set(n.id, []);
    outLinks.set(n.id, []);
  });
  linkSpecs.forEach((l) => {
    outLinks.get(l.source)?.push(l);
    inLinks.get(l.target)?.push(l);
  });

  const sum = (links: FlowSankeyLinkSpec[]) => links.reduce((a, l) => a + l.value, 0);
  const valueById = new Map<string, number>();
  nodeSpecs.forEach((n) => {
    const derived = Math.max(sum(inLinks.get(n.id) ?? []), sum(outLinks.get(n.id) ?? []));
    valueById.set(n.id, n.value ?? derived);
  });

  const colSum = new Map<number, number>();
  nodeSpecs.forEach((n) => {
    colSum.set(n.col, (colSum.get(n.col) ?? 0) + (valueById.get(n.id) ?? 0));
  });
  const maxNodesInCol = Math.max(...cols.map((c) => nodeSpecs.filter((n) => n.col === c).length));
  const usable = height - topPad - bottomPad - gapPx * (maxNodesInCol - 1);
  const maxColSum = Math.max(...[...colSum.values()], 1e-9);
  const scale = maxColSum > 0 ? usable / maxColSum : 0;

  // Stack nodes per column, top to bottom.
  const layoutNodes: FlowSankeyLayoutNode[] = [];
  const nodeIndexById = new Map<string, number>();
  cols.forEach((c) => {
    let y = topPad;
    nodeSpecs
      .filter((n) => n.col === c)
      .forEach((n) => {
        const value = valueById.get(n.id) ?? 0;
        const rawHeight = value * scale;
        const heightPx = Math.max(rawHeight, floorPx);
        const y0 = y;
        const y1 = y0 + heightPx;
        y = y1 + gapPx;
        const x0 = colX.get(c) ?? 0;
        layoutNodes.push({
          ...n,
          value,
          x0,
          x1: x0 + nodeWidth,
          y0,
          y1,
          heightPx,
          floored: rawHeight < floorPx,
          negligible: value / maxColSum < NEGLIGIBLE_SHARE,
          labelY: y0, // refined by the collision pass below
          labelLines: 3, // refined by the collision pass below
        });
        nodeIndexById.set(n.id, layoutNodes.length - 1);
      });
  });

  // Allocate link slots down each node face, ordered by the other end's y0
  // (keeps ribbons from crossing at the node face).
  const outSlot = new Map<FlowSankeyLinkSpec, { y: number; h: number }>();
  const inSlot = new Map<FlowSankeyLinkSpec, { y: number; h: number }>();
  layoutNodes.forEach((n) => {
    const outs = [...(outLinks.get(n.id) ?? [])].sort(
      (a, b) => (layoutNodes[nodeIndexById.get(a.target) ?? 0]?.y0 ?? 0) - (layoutNodes[nodeIndexById.get(b.target) ?? 0]?.y0 ?? 0),
    );
    let y = n.y0;
    outs.forEach((l) => {
      const h = Math.max(l.value * scale, floorPx);
      outSlot.set(l, { y: y + h / 2, h });
      y += h;
    });

    const ins = [...(inLinks.get(n.id) ?? [])].sort(
      (a, b) => (layoutNodes[nodeIndexById.get(a.source) ?? 0]?.y0 ?? 0) - (layoutNodes[nodeIndexById.get(b.source) ?? 0]?.y0 ?? 0),
    );
    y = n.y0;
    ins.forEach((l) => {
      const h = Math.max(l.value * scale, floorPx);
      inSlot.set(l, { y: y + h / 2, h });
      y += h;
    });
  });

  const layoutLinks: FlowSankeyLayoutLink[] = linkSpecs.map((l) => {
    const sIdx = nodeIndexById.get(l.source);
    const tIdx = nodeIndexById.get(l.target);
    const sourceNode = sIdx !== undefined ? layoutNodes[sIdx] : undefined;
    const targetNode = tIdx !== undefined ? layoutNodes[tIdx] : undefined;
    const sSlot = outSlot.get(l) ?? { y: sourceNode?.y0 ?? 0, h: floorPx };
    const tSlot = inSlot.get(l) ?? { y: targetNode?.y0 ?? 0, h: floorPx };
    const x0 = (sourceNode?.x1 ?? 0) + 4;
    const x1 = (targetNode?.x0 ?? 0) - 4;
    const mx = (x0 + x1) / 2;
    const h1 = sSlot.h / 2;
    const h2 = tSlot.h / 2;
    const path = [
      `M ${x0} ${sSlot.y - h1}`,
      `C ${mx} ${sSlot.y - h1}, ${mx} ${tSlot.y - h2}, ${x1} ${tSlot.y - h2}`,
      `L ${x1} ${tSlot.y + h2}`,
      `C ${mx} ${tSlot.y + h2}, ${mx} ${sSlot.y + h1}, ${x0} ${sSlot.y + h1} Z`,
    ].join(' ');
    return {
      ...l,
      path,
      heightPx: sSlot.h,
      floored: l.value * scale < floorPx,
      negligible: l.value / maxColSum < NEGLIGIBLE_SHARE,
    };
  });

  // Per-column label collision pass (SIL-633). Three sweeps, because the old
  // single push-down sweep clamped every overflowing label to the same
  // `height - block` line and drew them on top of each other — exactly what a
  // column ending in two floored ~0 nodes produces (circularity's
  // "Retained in fleet" / "Cat-5 disposal burden").
  cols.forEach((c) => {
    const colNodes = layoutNodes.filter((n) => n.col === c); // already top-down
    // 1. push down to clear the block above.
    let minY = -Infinity;
    colNodes.forEach((n) => {
      n.labelY = Math.max(n.y0 + Math.min(10, Math.max(4, n.heightPx / 2)), minY);
      minY = n.labelY + desiredLabelLines(n) * LABEL_LINE_H + 2;
    });
    // 2. pull back up whatever ran off the bottom, bottom-up, never closer
    // than one line to the neighbour below.
    for (let i = colNodes.length - 1; i >= 0; i--) {
      const below = colNodes[i + 1];
      const maxY = below ? below.labelY - LABEL_MIN_BLOCK_PX : height - LABEL_LINE_H + 2;
      const n = colNodes[i];
      if (n) n.labelY = Math.max(LABEL_LINE_H, Math.min(n.labelY, maxY));
    }
    // 3. spend the surviving gap on as many lines as it fits.
    colNodes.forEach((n, i) => {
      const gap = (colNodes[i + 1]?.labelY ?? height + 2) - n.labelY;
      n.labelLines = Math.max(1, Math.min(desiredLabelLines(n), Math.floor(gap / LABEL_LINE_H)));
    });
  });

  // SIL-632 — horizontal label budget, uniform per column: the gap from the
  // label's start to the next column's nodes. The last column budgets against
  // the right margin the caller already reserved for direct labels.
  cols.forEach((c, i) => {
    const nextCol = cols[i + 1];
    const nextX = nextCol === undefined ? width : colX.get(nextCol) ?? width;
    const labelX = (colX.get(c) ?? 0) + nodeWidth + LABEL_OFFSET_PX;
    const budget = nextX - labelX - LABEL_GUTTER_PX;
    layoutNodes
      .filter((n) => n.col === c)
      .forEach((n) => {
        n.labelDisplay = n.label == null ? undefined : truncateToWidth(n.label, budget);
        n.subTextDisplay = n.subText == null ? undefined : truncateToWidth(n.subText, budget, SUBTEXT_FONT_PX);
        n.capDisplay = n.cap == null ? undefined : truncateToWidth(n.cap, budget, CAP_FONT_PX);
      });
  });

  return { nodes: layoutNodes, links: layoutLinks, width, height, cols, colX, scale };
}
